import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const AIDigitalMarketing2025 = ({ name }) => {
  const [showScroll, setShowScroll] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    // Scroll to top on mount or when name changes
    window.scrollTo(0, 0);
  }, [name]);

  useEffect(() => {
    // Initialize WOW.js for animations
    if (typeof window !== 'undefined' && window.WOW) {
      new window.WOW().init();
    }

    // Google Analytics
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      gtag('js', new Date());
      gtag('config', 'G-P2TS4436R5');
    }
  }, []);

  // Scroll progress + back-to-top visibility
  useEffect(() => {
    const handleScrollProgress = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 307.919 : 0;

      setScrollProgress(progress);
      setShowScroll(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScrollProgress);
    handleScrollProgress();

    return () => window.removeEventListener('scroll', handleScrollProgress);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80; // Adjust for fixed navbar height
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth',
      });
    }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>MASTERING DIGITAL MARKETING WITH AI TOOLS IN 2025</title>
        <meta
          name="description"
          content="Master digital marketing in 2025 using AI tools like SEMrush, Ahrefs & ChatGPT. Boost productivity, SEO & results with smart strategies."
        />
      </Helmet>

      {/* Navbar */}
      <Navbar />

      {/* Hero Section with Banner Image */}
      <header className="relative w-full">
        <div
          className="relative w-full h-64 sm:h-80 md:h-96 lg:h-[420px] bg-center bg-cover bg-no-repeat"
          style={{ backgroundImage: "url('../img/digital.jpg')" }}
        >
          {/* Dark overlay */}
          <div className="absolute inset-0  bg-opacity-60" />

          {/* Text content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white px-4">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-center">
              Mastering Digital Marketing with AI Tools in 2025
            </h1>
            <div className="flex items-center gap-2 text-sm sm:text-base">
              <Link to="/" className="hover:underline">
                Home
              </Link>
              <svg
                width="7"
                height="10"
                viewBox="0 0 7 10"
                fill="none"
                className="mt-1"
              >
                <path
                  d="M0.297 0.262C0.105 0.426 -0.002 0.648 0 0.878C0.002 1.109 0.113 1.329 0.308 1.492L4.02 4.604L0.366 7.711C0.173 7.875 0.067 8.097 0.069 8.328C0.071 8.558 0.182 8.778 0.377 8.941C0.474 9.022 0.59 9.086 0.717 9.13C0.844 9.174 0.981 9.196 1.118 9.195C1.255 9.194 1.391 9.171 1.518 9.126C1.644 9.081 1.758 9.016 1.854 8.933L6.232 5.212C6.329 5.131 6.405 5.034 6.457 4.927C6.509 4.821 6.535 4.706 6.534 4.591C6.533 4.476 6.505 4.362 6.451 4.256C6.397 4.15 6.319 4.054 6.221 3.974L1.774 0.254C1.677 0.173 1.561 0.109 1.434 0.065C1.307 0.021 1.171 -0.001 1.033 0C0.896 0.001 0.76 0.024 0.634 0.069C0.507 0.114 0.393 0.18 0.297 0.262Z"
                  fill="white"
                />
              </svg>
              <span>Mastering Digital Marketing with AI Tools in 2025</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content with Sidebar */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 py-16 lg:py-24 bg-gradient-to-b from-gray-50 to-gray-100 rounded-3xl shadow-md">
        <div className="flex flex-col lg:flex-row lg:gap-12">
          {/* Main Content */}
          <article className="lg:w-3/4 space-y-12">
            {/* Intro Block */}
            <header className="text-center mb-12">
              <h1
                className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 tracking-tight drop-shadow-md animate-fadeIn"
                data-wow-delay="0.1s"
              >
                Mastering Digital Marketing with AI Tools in 2025
              </h1>
              <p
                className="text-base sm:text-lg text-gray-600 mt-4 max-w-3xl mx-auto leading-relaxed animate-fadeIn"
                data-wow-delay="0.2s"
              >
                Mastering digital marketing in 2025 means knowing how to use AI tools. Today’s top marketers combine
                their strategic thinking with AI-powered tools like ChatGPT, Jasper and Surfer SEO to streamline content
                creation, automate campaigns and analyze data at scale. This blog explores how you can master digital
                marketing using the right AI tools to stay ahead in 2025.
              </p>

              {/* Overview Image Placeholder */}
              <div className="image-placeholder mt-8 flex justify-center">
                <img
                  src="/img/blog41.jpg"
                  alt="AI-powered digital marketing overview"
                />
              </div>
            </header>

            {/* 1. What Is AI in Digital Marketing? */}
            <section
              id="what-is-ai"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">
                What is AI in Digital Marketing?
              </h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4">
                In 2025, AI tools have become essential for digital marketing success. AI Digital Marketing refers to the
                integration of AI tools into digital marketing strategies to automate, optimize and scale performance.
                These tools support everything from content and SEO to ads and analytics.
              </p>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                Popular AI tools include:
              </p>
              <ul className="list-disc pl-6 text-base sm:text-lg text-gray-700 mt-3 space-y-2">
                <li>ChatGPT – AI-powered content creation and idea generation</li>
                <li>Surfer SEO – On-page SEO optimization and content structure</li>
                <li>Jasper – High-conversion copywriting for ads and landing pages</li>
                <li>HubSpot AI – CRM intelligence and marketing automation</li>
                <li>Adzooma – PPC management and ad performance optimization</li>
                <li>Grammarly – AI-enhanced writing, clarity and tone improvement</li>
              </ul>

              {/* Image Placeholder */}
              <div className="image-placeholder my-5 flex justify-center">
                <img
                  src="/img/blog42.jpg"
                  alt="AI tools used in digital marketing"
                />
              </div>
            </section>

            {/* 2. Key Benefits of Using AI Tools in Digital Marketing (2025) */}
            <section
              id="key-benefits"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">
                Key benefits of Using AI Tools in Digital Marketing (2025)
              </h2>

              <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mt-4">
                1. Bulk SEO Optimization with AI-Powered Tools
              </h3>
              <p className="text-base sm:text-lg text-gray-700 mt-2 leading-relaxed">
                Tools like Ubersuggest, Ahrefs, Semrush and WriterZen use AI to deliver fast insights for keyword
                research, content planning and competitor analysis. They help you optimize hundreds of pages quickly by
                surfacing the best keyword opportunities and on-page improvements.
              </p>

              <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mt-6">
                2. High-Quality Content Creation in Minutes
              </h3>
              <p className="text-base sm:text-lg text-gray-700 mt-2 leading-relaxed">
                With platforms like Jasper, ChatGPT and Writesonic, marketers can generate SEO-friendly blogs, ad copy,
                social posts and product descriptions in minutes. These tools support faster content production while
                keeping copy relevant, engaging and optimized for search.
              </p>

              <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mt-6">
                3. AI-Driven Ad Campaign Assistance
              </h3>
              <p className="text-base sm:text-lg text-gray-700 mt-2 leading-relaxed">
                Google Ads Performance Max and Meta’s Advantage+ use machine learning to auto-optimize budgets,
                creatives and targeting. This helps improve ROAS (Return on Ad Spend) by continuously testing and
                refining ads based on real-time performance data.
              </p>

              <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mt-6">
                4. Smarter Insights from GSC & GA4
              </h3>
              <p className="text-base sm:text-lg text-gray-700 mt-2 leading-relaxed">
                AI integrations with Google Search Console (GSC) and Google Analytics 4 (GA4) simplify data
                interpretation. They highlight ranking drops, traffic spikes and user behavior trends, helping you react
                faster and make decisions backed by accurate insights.
              </p>

              {/* Image Placeholder */}
              <div className="image-placeholder my-5 flex justify-center">
                <img
                  src="/img/blog43.jpg"
                  alt="Benefits of AI tools in digital marketing"
                />
              </div>
            </section>

            {/* 3. How to Master Digital Marketing with AI */}
            <section
              id="mastering-with-ai"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">
                How to Master Digital Marketing with AI
              </h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-3">
                AI tools are powerful, but real mastery comes from combining them with solid marketing fundamentals.
                Here’s how to get the most from AI in your daily digital marketing work:
              </p>
              <ul className="list-disc pl-6 text-base sm:text-lg text-gray-700 space-y-2">
                <li>
                  Use AI tools like Ahrefs, Ubersuggest and Semrush to simplify daily SEO, ad research and content
                  planning tasks.
                </li>
                <li>
                  Automate repetitive work such as posting, reporting, basic analysis and first-draft content creation
                  to save time.
                </li>
                <li>
                  Explore each tool deeply—advanced features often unlock huge gains in performance and productivity.
                </li>
                <li>
                  Practice on real projects to build hands-on experience and understand how AI recommendations impact
                  results.
                </li>
                <li>
                  Stay updated with new AI trends and platforms so your strategies remain competitive in an evolving
                  landscape.
                </li>
              </ul>

              {/* Image Placeholder */}
              <div className="image-placeholder my-5 flex justify-center">
                <img
                  src="/img/blog44.jpg"
                  alt="Marketer mastering digital marketing with AI tools"
                />
              </div>
            </section>

            {/* 4. Common Mistakes to Avoid */}
            <section
              id="common-mistakes"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">
                Common mistakes to avoid
              </h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-3">
                Relying too heavily on AI tools without understanding core marketing strategy can harm your brand and
                results. AI should support your thinking—not replace it.
              </p>
              <ul className="list-disc pl-6 text-base sm:text-lg text-gray-700 space-y-2">
                <li>
                  Publishing AI-generated content without human editing, which can hurt SEO, relevance and user
                  engagement.
                </li>
                <li>
                  Ignoring brand voice and tone, leading to generic or inconsistent messaging across channels.
                </li>
                <li>
                  Failing to properly use SEO tools like Semrush or WriterZen, resulting in weak keyword targeting and
                  missed ranking opportunities.
                </li>
                <li>
                  Over-automating communication and losing the human connection that builds trust with customers.
                </li>
              </ul>

              {/* Image Placeholder */}
              <div className="image-placeholder my-5 flex justify-center">
                <img
                  src="/img/blog45.jpg"
                  alt="Common mistakes with AI in marketing"
                />
              </div>
            </section>

            {/* 5. Why Choose AI Digital Marketing Over Traditional Methods? */}
            <section
              id="why-ai-over-traditional"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">
                Why choose AI Digital Marketing over traditional methods?
              </h2>
              <ul className="list-disc pl-6 text-base sm:text-lg text-gray-700 space-y-2">
                <li>
                  <strong>Outdated Methods:</strong> Newspaper ads, TV commercials and pamphlets often miss today’s
                  digital-first audience.
                </li>
                <li>
                  <strong>Lack of Personalization:</strong> Traditional marketing relies on generic messaging, while AI
                  enables hyper-targeted campaigns.
                </li>
                <li>
                  <strong>High Costs, Low ROI:</strong> Print and TV ads are costly with limited tracking; AI tools
                  optimize spend with measurable results.
                </li>
                <li>
                  <strong>Slow Execution:</strong> Offline campaigns can take days or weeks to launch; AI tools deliver
                  real-time insights and rapid adjustments.
                </li>
                <li>
                  <strong>Data-Driven Strategies:</strong> AI-driven marketing uses live data to refine campaigns
                  continuously, improving performance with every iteration.
                </li>
              </ul>

              {/* Image Placeholder */}
              {/* <div className="image-placeholder my-5 flex justify-center">
                <img
                  src="/img/blog46.jpg"
                  alt="AI digital marketing versus traditional marketing"
                />
              </div> */}
            </section>

            {/* 6. AI vs Traditional Digital Marketing */}
            <section
              id="ai-vs-traditional"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">
                AI vs Traditional Digital Marketing: Why AI is the smarter move
              </h2>
              <ul className="list-disc pl-6 text-base sm:text-lg text-gray-700 space-y-2">
                <li>
                  <strong>Automation vs Manual Work:</strong> AI tools automate SEO, content and ad tasks, saving time
                  and boosting efficiency.
                </li>
                <li>
                  <strong>Faster Data Decisions:</strong> AI analyzes real-time data to optimize campaigns instantly,
                  instead of waiting for manual reports.
                </li>
                <li>
                  <strong>Market Shift in 2025:</strong> Brands now expect AI-driven strategies for better performance,
                  personalization and ROI.
                </li>
                <li>
                  <strong>Smarter Tools, Better Output:</strong> Platforms like Semrush, Ubersuggest and WriterZen make
                  complex tasks easier to manage.
                </li>
                <li>
                  <strong>Stronger Results:</strong> AI helps deliver higher rankings, engagement and conversions with
                  less manual effort.
                </li>
              </ul>
            </section>

            {/* 7. People Also Ask */}
            <section
              id="people-also-ask"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">
                People also ask
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
                    1. Mastering digital marketing with AI tools free
                  </h3>
                  <p className="text-base sm:text-lg text-gray-700 mt-2 leading-relaxed">
                    Start by learning the basics of SEO, ads and content. Then use free AI tools like ChatGPT, Canva and
                    Google Keyword Planner on real projects to practice, test ideas and improve your skills over time.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
                    2. Free AI tools for marketing
                  </h3>
                  <p className="text-base sm:text-lg text-gray-700 mt-2 leading-relaxed">
                    ChatGPT, Canva, Google Keyword Planner, Pictory, Copy.ai, Grammarly and Buffer are popular free or
                    freemium AI tools that support content creation, SEO research, design, video and social media
                    scheduling.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
                    3. AI tools for digital marketing
                  </h3>
                  <p className="text-base sm:text-lg text-gray-700 mt-2 leading-relaxed">
                    Examples include Jasper AI for content, SurferSEO for SEO, Canva AI for design, Pictory for video,
                    Hootsuite AI for social media and HubSpot AI for CRM and email automation.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
                    4. Mastering digital marketing in an AI world
                  </h3>
                  <p className="text-base sm:text-lg text-gray-700 mt-2 leading-relaxed">
                    Combine strong marketing fundamentals with AI skills. Stay updated on trends, experiment with new
                    tools and always balance automation with human creativity and strategic thinking.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
                    5. Cons of AI in digital marketing
                  </h3>
                  <p className="text-base sm:text-lg text-gray-700 mt-2 leading-relaxed">
                    AI can lack human emotion, depends heavily on data quality, may generate generic content, raises
                    privacy concerns and often requires paid plans for advanced features.
                  </p>
                </div>
              </div>
            </section>

            {/* 8. Conclusion */}
            <section
              id="conclusion"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">Conclusion</h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-3">
                Mastering digital marketing in 2025 means leveraging AI tools like Ahrefs, Ubersuggest and Google Search
                Console for faster SEO, smarter targeting and better results. These tools help you move beyond guesswork
                and build campaigns based on real data.
              </p>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-3">
                As trends like predictive marketing, automation and voice search grow, staying ahead requires blending
                human creativity with AI-powered strategy. Marketers who adapt, learn and experiment with AI will lead
                the future of digital marketing.
              </p>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                Use AI as your assistant—not your replacement—and you’ll be ready to build campaigns that are faster,
                smarter and more impactful than ever.
              </p>
            </section>
          </article>

          {/* Sidebar */}
          <aside className="lg:w-1/4 mt-12 lg:mt-0">
            <div className="sticky top-24 bg-white rounded-2xl shadow-lg p-8 bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200">
              <h3 className="text-xl font-semibold text-gray-800 mb-6">Quick Navigation</h3>
              <ul className="space-y-4">
                {[
                  { id: 'what-is-ai', label: 'What Is AI in Digital Marketing?' },
                  { id: 'key-benefits', label: 'Key Benefits of AI (2025)' },
                  { id: 'mastering-with-ai', label: 'How to Master with AI' },
                  { id: 'common-mistakes', label: 'Common Mistakes to Avoid' },
                  { id: 'why-ai-over-traditional', label: 'Why AI over Traditional' },
                  { id: 'ai-vs-traditional', label: 'AI vs Traditional Marketing' },
                  { id: 'people-also-ask', label: 'People Also Ask' },
                  { id: 'conclusion', label: 'Conclusion' },
                ].map((item, index) => (
                  <li key={item.id} className="animate-slideInRight" data-wow-delay={`${0.1 * index}s`}>
                    <button
                      onClick={() => scrollToSection(item.id)}
                      className="text-blue-600 hover:text-blue-800 transition-colors text-base font-medium hover:bg-blue-100 rounded-md px-3 py-2 w-full text-left hover:shadow-md"
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Scroll to Top with Progress Ring */}
      {showScroll && (
        <div
          onClick={scrollToTop}
          className="scroll-top fixed bottom-6 right-6 z-50 cursor-pointer flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-blue-500 to-blue-700 shadow-lg hover:from-blue-600 hover:to-blue-800 transition-all"
        >
          <svg
            className="progress-circle -rotate-90 w-12 h-12 absolute"
            viewBox="-1 -1 102 102"
          >
            <path
              d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98"
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeDasharray="307.919"
              strokeDashoffset={307.919 - scrollProgress}
              style={{ transition: 'stroke-dashoffset 0.1s linear' }}
            />
          </svg>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-6 h-6 text-white relative z-10"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </div>
      )}

      <style jsx>{`
        @keyframes slideInDown {
          0% {
            opacity: 0;
            transform: translateY(-20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeIn {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        @keyframes slideUp {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideInRight {
          0% {
            opacity: 0;
            transform: translateX(20px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes gradientShift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        .animate-gradientShift {
          background-size: 200% 200%;
          animation: gradientShift 8s ease infinite;
        }
        a,
        .link {
          text-decoration: none;
        }
        .image-placeholder {
          margin: 20px 0;
          display: flex;
          justify-content: center;
        }
        .image-placeholder img {
          max-width: 500px;
          height: auto;
          object-fit: cover;
          border-radius: 10px;
        }
        @media (max-width: 768px) {
          .image-placeholder img {
            max-width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default AIDigitalMarketing2025;
